import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, Search, CheckCircle2, Clock, XCircle, ShieldAlert, User, Mail, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function ReadyPill({ ready, waiverStatus, guardianStatus, isMinor }) {
  if (waiverStatus === 'declined' || guardianStatus === 'declined') {
    return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-red-600 text-white px-3 py-1.5 rounded-full">
      <XCircle size={14} /> DENIED
    </div>;
  }
  if (ready) {
    return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-emerald-600 text-white px-3 py-1.5 rounded-full">
      <CheckCircle2 size={14} /> READY
    </div>;
  }
  return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-amber-600 text-white px-3 py-1.5 rounded-full">
    <Clock size={14} /> {isMinor ? 'GUARDIAN PENDING' : 'PENDING'}
  </div>;
}

export default function CheckInSearch() {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load once on mount + whenever debounced query changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = debounced ? { q: debounced } : {};
        // Parallel: escape-room waivers + haunted-house waivers
        const [escR, hhR] = await Promise.all([
          axios.get(`${API}/api/admin/waivers`, { headers: authHeaders(), params }).catch(() => ({ data: { waivers: [] } })),
          axios.get(`${API}/api/admin/hh/waivers`, { headers: authHeaders(), params }).catch(() => ({ data: { waivers: [] } })),
        ]);
        const escaped = (escR.data.waivers || []).map(w => ({ ...w, _kind: 'escape' }));
        const haunted = (hhR.data.waivers || []).map(w => ({ ...w, _kind: 'haunted', signer_name: w.leader_name || w.signer_name }));
        if (!cancel) setWaivers([...escaped, ...haunted]);
      } catch {
        if (!cancel) setWaivers([]);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [debounced]);

  // Flatten waivers → one row per guest, with client-side name filter for extra precision
  const rows = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    const list = [];
    for (const w of waivers) {
      const guests = w.guests || [];
      for (const g of guests) {
        // Client-side name filter (server already loosely matched; this narrows for exact-name typing)
        if (q && !(g.name || '').toLowerCase().includes(q)
              && !(w.signer_name || '').toLowerCase().includes(q)
              && !(w.email || '').toLowerCase().includes(q)
              && !(w.waiver_id || '').toLowerCase().includes(q)) continue;
        const isMinor = !!g.is_minor;
        // Adults are ready as soon as the waiver is on file (any non-declined state).
        // Minors are only ready once THEIR guardian has approved.
        // Prior bug: adults inherited the whole-waiver "pending_guardian" status.
        const notDeclined = w.status !== 'declined' && g.guardian_status !== 'declined';
        const ready = notDeclined && (isMinor ? g.guardian_status === 'approved' : true);
        // Per-row status (independent of siblings)
        const rowStatus = (w.status === 'declined' || g.guardian_status === 'declined')
          ? 'declined'
          : isMinor
            ? (g.guardian_status === 'approved' ? 'signed' : (g.guardian_status || 'pending_guardian'))
            : 'signed';
        list.push({
          key: `${w._kind || 'escape'}:${w.waiver_id}:${g.name}`,
          kind: w._kind || 'escape',
          name: g.name,
          is_minor: isMinor,
          guardian_status: g.guardian_status,
          guardian_name: g.guardian_name,
          guardian_email: g.guardian_email,
          waiver_id: w.waiver_id,
          signer_name: w.signer_name,
          email: w.email,
          phone: w.phone,
          order_num: w.order_num,
          submitted_at: w.submitted_at,
          waiver_status: rowStatus,
          ready,
        });
      }
    }
    // Sort: READY first (green), then pending, then denied; within each group most-recent first
    const rank = { true: 0, false: 1 };
    return list.sort((a, b) => {
      // Denied last
      const aDenied = a.waiver_status === 'declined' || a.guardian_status === 'declined';
      const bDenied = b.waiver_status === 'declined' || b.guardian_status === 'declined';
      if (aDenied !== bDenied) return aDenied ? 1 : -1;
      if (a.ready !== b.ready) return rank[a.ready] - rank[b.ready];
      return new Date(b.submitted_at) - new Date(a.submitted_at);
    });
  }, [waivers, debounced]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      if (r.waiver_status === 'declined' || r.guardian_status === 'declined') acc.denied++;
      else if (r.ready) acc.ready++;
      else acc.pending++;
      return acc;
    }, { ready: 0, pending: 0, denied: 0 });
  }, [rows]);

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="text-orange-400" size={22} />
        <h2 className="font-heading text-2xl tracking-widest uppercase">Check-In Search</h2>
      </div>
      <p className="text-xs text-muted-foreground">Front-desk look-up. Type a guest's name (or signer / email / waiver ID) — matches show up instantly. One row per guest.</p>

      {/* Big search box */}
      <div className="rounded-2xl border-2 border-orange-500/50 bg-black/60 p-2 flex items-center gap-3"
        style={{ boxShadow: '0 0 20px rgba(255,120,20,0.25)' }}>
        <Search size={20} className="text-orange-400 ml-2" />
        <input
          autoFocus
          data-testid="checkin-search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Type a guest name…"
          className="flex-1 bg-transparent outline-none text-lg py-2 pr-3 font-sans" />
        {query && (
          <button onClick={() => setQuery('')} data-testid="checkin-clear"
            className="px-3 text-xs text-muted-foreground hover:text-white">Clear</button>
        )}
      </div>

      {/* Live status chips */}
      <div className="flex gap-2 flex-wrap text-xs" data-testid="checkin-totals">
        <div className="font-heading tracking-widest uppercase bg-emerald-950/40 border border-emerald-500/40 text-emerald-400 px-3 py-1 rounded-full">Ready · {totals.ready}</div>
        <div className="font-heading tracking-widest uppercase bg-amber-950/40 border border-amber-500/40 text-amber-400 px-3 py-1 rounded-full">Pending · {totals.pending}</div>
        <div className="font-heading tracking-widest uppercase bg-red-950/40 border border-red-500/40 text-red-400 px-3 py-1 rounded-full">Denied · {totals.denied}</div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Searching…</p> : (
        rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <User size={40} className="mx-auto text-neutral-700 mb-3" />
            <p className="font-heading text-sm tracking-widest uppercase">
              {debounced ? 'No matches' : 'Start typing a name to look up a guest'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="checkin-results">
            {rows.map(r => (
              <div key={r.key} data-testid={`checkin-row-${r.waiver_id}`}
                className="rounded-xl border border-border bg-card p-3 sm:p-4 flex items-center gap-3 hover:border-orange-500/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg tracking-wide truncate">{r.name}</span>
                    {r.is_minor && <span className="font-heading text-[9px] tracking-widest uppercase bg-yellow-950/60 border border-yellow-500/40 text-yellow-300 px-1.5 py-0.5 rounded">MINOR</span>}
                    <span className={`font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${
                      r.kind === 'haunted'
                        ? 'bg-red-950/60 border border-red-500/40 text-red-300'
                        : 'bg-orange-950/60 border border-orange-500/40 text-orange-300'
                    }`}>
                      {r.kind === 'haunted' ? 'HAUNTED HOUSE' : 'ESCAPE ROOM'}
                    </span>
                    {r.order_num && (
                      <span className="font-mono text-[10px] text-orange-300 bg-orange-950/40 border border-orange-500/40 px-1.5 rounded">
                        {r.order_num}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><User size={11} /> signed by {r.signer_name || '—'}</span>
                    <span className="inline-flex items-center gap-1"><Mail size={11} /> {r.email}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}</span>
                    <span className="font-mono text-[10px] text-neutral-500">{r.waiver_id}</span>
                  </div>
                  {r.is_minor && r.guardian_status === 'approved' && r.guardian_name && (
                    <div className="text-[10px] text-emerald-300 mt-0.5">
                      <ShieldAlert size={9} className="inline mr-1" />Guardian: {r.guardian_name}
                    </div>
                  )}
                  {r.is_minor && r.guardian_status === 'pending' && (
                    <div className="text-[10px] text-amber-300 mt-0.5">
                      <ShieldAlert size={9} className="inline mr-1" />Guardian email sent to {r.guardian_email} · awaiting reply
                    </div>
                  )}
                </div>
                <ReadyPill ready={r.ready} waiverStatus={r.waiver_status} guardianStatus={r.guardian_status} isMinor={r.is_minor} />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
